import { createEntityAdapter, EntityState } from '@ngrx/entity'
import { createReducer, createSelector, on } from '@ngrx/store'
import { TableKey } from '@tradecafe/types/core'
import { isEqual, pick } from 'lodash-es'
import { InvoiceRow } from 'src/api/invoice'
import { ElasticSearchFilters } from 'src/services/elastic-search'
import { State } from '../reducers'
import { prepareInvoicesPayload } from './invoice-view-filters.utils'
import * as InvoiceViewActions from './invoice-view.actions'
import { InvoiceTotals } from '@tradecafe/types/core/search-service'

export const invoiceViewsFeatureKey = 'invoiceViews'
export interface InvoiceViewsState extends EntityState<InvoiceRow> {
  filters: Dictionary<ElasticSearchFilters>
  prevQuery: Dictionary<any>

  // invoice ids lists grouped by table key
  tables: Dictionary<string[]>
  // total invoices lists grouped by table key
  totals: Dictionary<InvoiceTotals>
}
const selectId = invoice => invoice.invoice_id
const adapter = createEntityAdapter<InvoiceRow>({ selectId })

const initialState: InvoiceViewsState = adapter.getInitialState({
  tables: {},
  totals: {},
  filters: {},
  prevQuery: {},
})


export const invoiceViewReducer = createReducer(
  initialState,
  on(InvoiceViewActions.loadInvoiceViews,
    (state, action) => {
      const key = action.tableKey
      const query = prepareInvoicesPayload(action.filters, action.page)
      const prev = state.prevQuery[key] || {}

      let { tables, totals, prevQuery } = state
      if (!isEqual(query.query, prev.query)) {
        tables = { ...tables, [key]: undefined } // drop table
        totals = { ...totals, [key]: undefined } // clear total
      } else if (!isEqual(query.sort, prev.sort)) {
        const table = [...tables[key] || []]
        tables = { ...tables, [key]: table.fill(undefined) } // clear table
      }
      // Record the latest dispatched request so query changes can invalidate stale responses.
      // Success handlers intentionally ignore skip when checking freshness because different pages
      // for the same query populate different slots in the same sparse table.
      prevQuery = { ...prevQuery, [key]: query }
      return { ...state, tables, totals, prevQuery }
    }),
  on(InvoiceViewActions.loadInvoiceViewsSuccess,
    (state, action) => {
      const key = action.tableKey
      const expected = state.prevQuery[key]
      const payload = action.payload
      const payloadKey = pick(payload, ['query', 'sort', 'limit', 'columns'])
      const expectedKey = expected && pick(expected, ['query', 'sort', 'limit', 'columns'])
      if (!expected || !isEqual(payloadKey, expectedKey)) {
        return state
      }

      const { total, totals: totals_, invoiceViews, resetTable } = action
      const { skip } = payload
      const invoiceIds = invoiceViews.map(selectId)

      const existing = state.tables[key]
      let table: string[]
      if (resetTable || existing == null) {
        table = Array.from<string>({ length: total }).fill(undefined)
      } else if (existing.length === total) {
        table = [...existing]
      } else {
        // Total changed between requests (e.g. concurrent loads / index refresh) — keep
        // already-resolved slots so page 0 is not wiped when a later page response arrives first
        // or reports a different count.
        table = Array.from({ length: total }, (_, i) => existing[i])
      }

      if (skip == null || skip < 0) {
        return state
      }

      let insertCount: number
      let idsToInsert: string[]
      let viewsToInsert: InvoiceRow[]
      if (table.length === 0) {
        // total === 0: only skip 0 is valid; nothing to splice.
        if (skip !== 0) return state
        insertCount = 0
        idsToInsert = []
        viewsToInsert = []
      } else if (skip >= table.length) {
        return state
      } else {
        const end = Math.min(table.length, skip + invoiceIds.length)
        insertCount = end - skip
        idsToInsert = invoiceIds.slice(0, insertCount)
        viewsToInsert = invoiceViews.slice(0, insertCount)
      }

      const nextTable = [...table]
      nextTable.splice(skip, insertCount, ...idsToInsert)
      const tables = { ...state.tables, [key]: nextTable }
      const totals = { ...state.totals, [key]: totals_ }

      state = adapter.upsertMany(viewsToInsert, state)
      return { ...state, tables, totals }
    }),
  on(InvoiceViewActions.loadInvoiceFiltersSuccess,
    (state, action) => {
      const key = action.tableKey
      const filters = { ...state.filters, [key]: action.result }
      return { ...state, filters }
    }),
)

const selectState = (state: State) => state[invoiceViewsFeatureKey]
export const { selectEntities: selectInvoiceViewEntities } = adapter.getSelectors(selectState)

export const selectInvoice = createSelector(
  selectInvoiceViewEntities,
  (invoiceViews: Dictionary<InvoiceRow>, invoiceId: string) =>
    invoiceViews[invoiceId])

export const selectInvoicesByIds = createSelector(
  selectInvoiceViewEntities,
  (invoiceViews: Dictionary<InvoiceRow>, invoiceIds: string[]) =>
    invoiceIds.map(invoiceId => invoiceViews[invoiceId]))

const selectTables = createSelector(selectState, state => state.tables)

export const selectInvoicesFilters = createSelector(
  selectState,
  (state: InvoiceViewsState, key: TableKey) => state.filters[key],
)

export const selectInvoicesTotals = createSelector(
  selectState,
  (state: InvoiceViewsState, key: TableKey) => state.totals[key],
)

export const selectInvoicesTable = createSelector(
  selectInvoiceViewEntities,
  selectTables,
  (invoiceViews: Dictionary<InvoiceRow>, tables: Dictionary<string[]>, key: TableKey) =>
    tables[key] ? tables[key].map(id => invoiceViews[id]) : undefined)

